import * as React from "react";
import {
  Box, Checkbox, Chip, FormControl,
  Grid, InputLabel, ListItemText, MenuItem,
  Paper, TextField, Typography,
} from "@mui/material";
import {ErrorOutline} from "@mui/icons-material";
import {translate} from "../../../translate/translate";
import {useParams} from "react-router-dom";
import {FtBomService} from "../../../services/ft-bom-service";
import {useEffect, useState} from "react";
import {MountRma, originOptions} from "../../../models/basic/ft-bom";
import {ErrorComponent} from "../../../components/error/error.component";
import TableActionComponent from "../../../components/table/table-action.component";
import TablePaginationComponent from "../../../components/table/table-pagination.component";
import {Form, Formik} from "formik";
import Select from "@mui/material/Select";

type mountRmaProps = {
    doneMount: (done: boolean) => void;
    changeActive: (done: boolean) => void;
    hasPermission: boolean;
}

const MountRmaComponent: React.FC<mountRmaProps> = ({
    doneMount,
    changeActive,
    hasPermission
}) => {
    const params = useParams();
    const idClient = params.idClient;
    const ftbom = params.ftbom;

    const service = new FtBomService();
    const [loading, setLoading] = useState(false);
    const [dataSourceMountRma, setDataSourceMountRma] = React.useState<MountRma[]>();
    const [auxDataSourceMountRma, setAuxDataSourceMountRma] = useState<MountRma[]>([]);
    const [selectedMountRma, setSelectedMountRma] = useState<MountRma[]>([]);
    const [disabledConfirm, setDisabledConfirm] = useState(false);

    const [groupsSearch, setGroupsSearch] = useState<string[]>([]);
    const [selectedGroupSearch, setSelectedGroupSearch] = useState<string[]>([]);
    const [quantitySented, setQuantitySent] = useState<{[key: number]: number}>();

    const [search, setSearch] = useState("");

    useEffect(() => {
        loadMountRma();
    }, []);

    useEffect(() => {
        loadMountRma(false);
    }, [selectedGroupSearch, search]);

    const loadMountRma = (initialize = true) => {
        if (idClient && ftbom) {
            setLoading(true);
            service.get_mount(idClient, ftbom, 'rma', selectedGroupSearch, search).then((response) => {

                const allMounts = response.data;
                const selected = allMounts.filter((mountRma: MountRma) => mountRma.used_in_rma === true);
                setDataSourceMountRma(allMounts);


                if (initialize) {

                    setSelectedMountRma(selected);
                    setAuxDataSourceMountRma(allMounts);
                    changeActive(response.data[0].active);

                    const groups = response.data?.map((it: any) => it.alternative_group_bom);
                    // @ts-ignore
                    const uniqueGroups = [...new Set(groups)];
                    setGroupsSearch(uniqueGroups);
                    setSelectedGroupSearch(uniqueGroups);

                    const quantitySentMap: {[key: number]: number} = {};
                    allMounts.forEach((mountRma: MountRma) => {
                        quantitySentMap[mountRma.id] = mountRma.quantity_sent_rma ? mountRma.quantity_sent_rma : mountRma.total_rma;
                    });
                    setQuantitySent(quantitySentMap);
                }


            }).catch((error) => {
                ErrorComponent(error);
            })
            .finally(() => {
                setLoading(false);
            });
        }
    }

    const changeSelectedMountRma = (mountRma: MountRma) => {
        setSelectedMountRma((prevSelected) => {
            const isSelected = prevSelected.find((item) => item.id === mountRma.id);
            if (isSelected) {
                const mount = mountRma;
                mount.used_in_rma = false;
                mount.quantity_sent_rma = quantitySented?.[mountRma.id];
                saveMountRma([mount]);
                return prevSelected.filter((item) => item.id !== mountRma.id);
            } else {
                const filtered = prevSelected.filter(
                    (item) => item.alternative_group_bom !== mountRma.alternative_group_bom
                );
                const sameGroup = dataSourceMountRma!.filter(
                  (item) => item.alternative_group_bom === mountRma.alternative_group_bom && item.id !== mountRma.id
                ).map(obj => ({...obj, used_in_rma: false}));

                const mount = mountRma;
                mount.used_in_rma = true;
                mount.quantity_sent_rma = quantitySented?.[mountRma.id];
                saveMountRma([...sameGroup, mount]);
                return [...filtered, mountRma];
            }
        });
    };

    const saveMountRma = (mountRma: MountRma[]) => {
        service.save_mount(mountRma, 'rma')
            .then((response) => {
                doneMount(response.data.status_mount);
            })
            .catch((error: any) => {
                ErrorComponent(error);
            })
    }

    const customCellRender = (column: string, index: number, row: MountRma) => {
        if (column === "used_in_rma") {
            const isSelected = selectedMountRma.some((item) => item.id === row.id);
            return (
                <Checkbox
                    checked={isSelected}
                    onChange={() => changeSelectedMountRma(row)}
                    disabled={!row.active || !hasPermission}
                />
            );
        }
        if (column === "origin") {
            const origin = originOptions.find(option => option.id === row.origin);
            return (
                <Chip size="small"
                      color={origin!.color as any}
                      label={origin!.label}
                />
            );
        }
        if (column === "alternative_group_bom") {
            return (
                <Typography variant="body2" color="textSecondary"
                            sx={{display: 'flex', alignItems: 'center', gap: '0.4rem'}}
                >
                    <ErrorOutline sx={{color: isGroupNotSelected(row.alternative_group_bom) ? 'orange': 'transparent', marginBottom: '0.2rem', marginRight: '0.4rem'}}/>
                    {row.alternative_group_bom || "-"}
                </Typography>
            );
        }
        if (column === "quantity_sent_rma") {
            return (
                <Formik
                    enableReinitialize={true}
                    validateOnMount={true}
                    initialValues={{
                        quantity_sent: row.quantity_sent_rma || 0,
                    }}
                    onSubmit={(values, {setSubmitting}) => {}}
                >
                    {
                        () => (
                            <Form autoComplete="off">
                                <Grid alignItems="center" container spacing={2} direction={"row"}>
                                    <Grid>
                                        <TextField
                                            name="quantity_sent_rma"
                                            variant="outlined"
                                            fullWidth
                                            type="number"
                                            value={quantitySented?.[row.id] ?? row.quantity_sent_rma}
                                            error={quantitySented && quantitySented[row.id] < row.total_rma}
                                            onChange={(event) => {
                                                const parsed = parseInt(event.target.value, 10);
                                                if (parsed < 0 || isNaN(parsed) || event.target.value.length > 10) return;
                                                setQuantitySent((prev) => ({
                                                    ...prev,
                                                    [row.id]: parsed
                                                }));
                                            }}
                                            onBlur={(event) => {
                                                const quantity = quantitySented ? quantitySented[row.id] : 0;
                                                setQuantitySent((prev) => ({
                                                    ...prev,
                                                    [row.id]: quantity
                                                }));
                                                const mount = row;
                                                mount.quantity_sent_rma = quantity;
                                                saveMountRma([mount]);
                                            }}
                                            inputProps={{
                                                sx: {
                                                    maxHeight: '0px !important',
                                                    height: '0px !important',
                                                },
                                                min: row.total_rma
                                            }}
                                            disabled={!row.active || !hasPermission}
                                        />
                                    </Grid>
                                </Grid>
                            </Form>
                    )}
                </Formik>
            );
        }
        return "-";
    };

    const cancel = () => {
        window.history.back();
    }

    const allGroupSelected = () => {
        const groups = auxDataSourceMountRma?.map(it => it.alternative_group_bom);
        // @ts-ignore
        const uniqueGroups = [...new Set(groups)];
        return uniqueGroups.every(group => selectedMountRma.some(it => it.alternative_group_bom === group));
    }

    const selectedGroups = () => {
        const groups = auxDataSourceMountRma?.map(it => it.alternative_group_bom);
        // @ts-ignore
        const uniqueGroups = [...new Set(groups)];
        return uniqueGroups.filter(group => selectedMountRma.some(it => it.alternative_group_bom === group));
    }

    const notSelectedGroups = () => {
        const groups = auxDataSourceMountRma?.map(it => it.alternative_group_bom);
        // @ts-ignore
        const uniqueGroups = [...new Set(groups)];
        return uniqueGroups.filter(group => !selectedMountRma.some(it => it.alternative_group_bom === group));
    }

    const allSearchGroupSelected = () => {
        return groupsSearch.length === selectedGroupSearch.length;
    }

    const allPendingGroupsSelected = () => {
        if (notSelectedGroups().length === 0) return false;
        if (selectedGroupSearch.length === 0) return false;
        if (notSelectedGroups().length !== selectedGroupSearch.length) return false;

        const sortedA = [...notSelectedGroups()].sort();
        const sortedB = [...selectedGroupSearch].sort();

        return sortedA.every((val, index) => val === sortedB[index]);
    }

    const isGroupNotSelected = (group: string) => {
        return notSelectedGroups().includes(group);
    }

    useEffect(() => {
        setDisabledConfirm(!allGroupSelected());
    }, [selectedMountRma]);

    const renderSelection = (selected: any, dataSource: any[], allSelected: () => boolean) => {
        if (allSelected()) {
            return translate.t('all');
        }

        if (allPendingGroupsSelected()) {
            return translate.t('pending_groups');
        }

        const sorted = selected
            .map((it: any) => dataSource.find(item => item === it))
            .filter(Boolean)
            .sort()

        return sorted.join(", ");
    };

    return (
        <>
            <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between"}}>
                <Typography style={{marginTop: "16px", width: "400px"}}>
                    <b>Qtde. de RMA para montagem:</b> {dataSourceMountRma ? dataSourceMountRma[0]?.quantity_rma : 0} unidade(s)
                </Typography>
                <Box sx={{display: "flex", alignItems: "center", gap: "10px", justifyContent: "flex-end", flexWrap: "wrap"}}>
                    <Grid width="280px">
                        <FormControl fullWidth>
                            <InputLabel>
                                Pesquisar por grupo de material
                            </InputLabel>
                            <Select
                                name="group"
                                label={"Pesquisar por grupo de material"}
                                variant={"outlined"}
                                fullWidth
                                aria-placeholder="Pesquisar por grupo de material"
                                autoFocus={false}
                                value={selectedGroupSearch}
                                onChange={(event: any) => {
                                    if (event.target.value.includes(0)) {
                                        if (!allSearchGroupSelected()) {
                                            setSelectedGroupSearch(groupsSearch.map((p: string) => p));
                                            return;
                                        }
                                        setSelectedGroupSearch([]);
                                        return;
                                    }
                                    if (event.target.value.includes(9999)) {
                                        if (!allPendingGroupsSelected()) {
                                            setSelectedGroupSearch(notSelectedGroups());
                                            return;
                                        }
                                        setSelectedGroupSearch([]);
                                        return;
                                    }
                                    setSelectedGroupSearch(event.target.value)
                                }}
                                multiple
                                renderValue={(selected) => renderSelection(selected, groupsSearch, allSearchGroupSelected)}
                                MenuProps={{
                                    PaperProps: {
                                        style: {
                                            maxHeight: '400px',
                                        },
                                    },
                                    disableAutoFocusItem: true,
                                }}
                                inputProps={{
                                    sx: {
                                        maxWidth: '400px !important',
                                        width: '400px !important',
                                    }
                                }}
                            >
                                <MenuItem key={0} value={0} disabled={loading}>
                                    <Checkbox checked={allSearchGroupSelected()}/>
                                    <ListItemText primary={
                                        <Box display="flex" alignItems="center" justifyContent="space-between">
                                            <Typography variant="body1">{translate.t('all')}</Typography>
                                            {allSearchGroupSelected() && <Typography variant="body2"
                                                                                     color="gray">{translate.t('clear')}</Typography>}
                                        </Box>
                                    }/>
                                </MenuItem>
                                {notSelectedGroups().length && (<MenuItem key={9999} value={9999} disabled={loading}>
                                    <Checkbox checked={allPendingGroupsSelected()}/>
                                    <ListItemText primary={
                                        <Box display="flex" alignItems="center" justifyContent="space-between">
                                            <Typography variant="body1">Grupos pendentes</Typography>
                                            {allPendingGroupsSelected() && <Typography variant="body2"
                                                                                       color="gray">{translate.t('clear')}</Typography>}
                                        </Box>
                                    }/>
                                </MenuItem>)}
                                {groupsSearch?.map((p: any) => (
                                    <MenuItem key={p} value={p} disabled={loading}>
                                        <Checkbox checked={selectedGroupSearch.includes(p)}/>
                                        <ListItemText primary={p}/>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid width="280px">
                        <FormControl fullWidth>
                            <TextField
                                sx={{minWidth: "150px !important"}}
                                name="search"
                                label={""}
                                fullWidth
                                variant={"outlined"}
                                autoFocus={false}
                                value={search}
                                placeholder="Pesquisar por material code e description"
                                onChange={(event: React.ChangeEvent<{
                                    value: string
                                }>) => {
                                    setSearch(event.target.value);
                                }}
                            />
                        </FormControl>
                    </Grid>
                </Box>
            </Box>
            <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "16px"}}>
                <Typography style={{marginTop: "16px"}}>
                    Selecione os materiais que devem ser utilizados na montagem da RMA:
                </Typography>
                <Typography style={{marginTop: "16px", alignItems: "center", display: "flex", marginRight: "10px"}}>
                    <ErrorOutline sx={{color: 'orange', marginBottom: '0.2rem', marginRight: '0.4rem'}}/>
                    <b>Grupos pendentes de seleção: &ensp;</b> {notSelectedGroups().length}
                </Typography>
            </Box>
            <Paper variant="outlined" sx={{marginTop: "16px"}}>
                <TablePaginationComponent
                    state={dataSourceMountRma}
                    displayedColumnsShow={["Select", "Alt Grp BoM", "Material Code", "Description EN", "Description PT", "Qty Usage", "Quantity", "Quantity Sent", "Origin"]}
                    displayedColumns={["used_in_rma", "alternative_group_bom", "foxconn_part_number", "description_english", "description_portuguese", "usage", "total_rma", "quantity_sent_rma", "origin"]}
                    numberColumns={["usage", "total_rma"]}
                    integerColumns={["usage", "total_rma"]}
                    actionsComponent={TableActionComponent}
                    loading={loading}
                    customColumns={["used_in_rma", "origin", "alternative_group_bom", "quantity_sent_rma"]}
                    customCellRender={customCellRender}
                    groupByColumn={"alternative_group_bom"}
                />
            </Paper>
        </>
    );
};

export default MountRmaComponent;
